import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  SheetImportFooter,
  SheetImportHistory,
  SheetImportOverlay,
  SheetImportUrlTabs,
} from '../../components/sheetImport/SheetImportParts';
import { formatDate } from './format';

const FIELD_LABELS = {
  location_scope: 'Location scope',
  scope_notes: 'Scope notes',
  pallet_max_units: 'Pallet max units',
  sm_bin_max_units: 'Small bin max units',
  lg_bin_max_units: 'Large bin max units',
  large_pallet_max_units: 'Large pallet max units',
  pallet_max_cartons: 'Pallet max cartons',
  units_per_carton: 'Units per carton',
  manual_capacity: 'Manual capacity',
  manual_exception: 'Manual exception',
  approval_state: 'Approval state',
  approval_owner: 'Approval owner',
  source: 'Source',
  governance_status: 'Status',
  notes: 'Notes',
};

const scopeOf = (item) => item?.location_scope || item?.record?.location_scope || '';
const familyLabel = (item) => (scopeOf(item) ? `${item.family} · ${scopeOf(item)}` : item.family);
const itemKey = (item) => `${item.family}-${scopeOf(item)}-${item.row}`;

const displayValue = (value) => {
  if (value == null || value === '') return '—';
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return String(value);
};

const CapacitySheetSyncModal = ({ isOpen, canEdit, onClose, onApplied }) => {
  const [url, setUrl] = useState('');
  const [tab, setTab] = useState('');
  const [tabs, setTabs] = useState([]);
  const [history, setHistory] = useState([]);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');

  const loadTabs = useCallback(async (workbookUrl, preferredTab) => {
    if (!workbookUrl) return;
    setLoadingTabs(true);
    setError('');
    try {
      const params = new URLSearchParams({ url: workbookUrl });
      const { data } = await axios.get(`/api/v1/warehouse/capacity-matrix/sheet-import/tabs?${params}`);
      const nextTabs = data.tabs || [];
      setTabs(nextTabs);
      const preferred = nextTabs.find((item) => item.title === preferredTab)
        || nextTabs.find((item) => item.title === 'Capacity Profiles')
        || nextTabs[0];
      setTab(preferred?.title || '');
    } catch (requestError) {
      setError(requestError.response?.data?.message
        || requestError.response?.data?.error
        || 'Unable to load spreadsheet tabs.');
    } finally {
      setLoadingTabs(false);
    }
  }, []);

  const loadState = useCallback(async () => {
    const { data } = await axios.get('/api/v1/warehouse/capacity-matrix/sheet-import');
    setHistory(data.history || []);
    setUrl(data.default_url || '');
    if (data.default_url) {
      await loadTabs(data.default_url, data.default_tab || 'Capacity Profiles');
    } else {
      setTab('');
      setTabs([]);
    }
  }, [loadTabs]);

  useEffect(() => {
    if (!isOpen) return;
    setPreview(null);
    setApplied(null);
    setError('');
    loadState().catch((requestError) => {
      setError(requestError.response?.data?.message
        || requestError.response?.data?.error
        || 'Unable to load sheet import history.');
    });
  }, [isOpen, loadState]);

  const previewSheet = async () => {
    setLoadingPreview(true);
    setError('');
    setApplied(null);
    try {
      const { data } = await axios.post('/api/v1/warehouse/capacity-matrix/sheet-import/preview', { url, tab });
      setPreview(data);
    } catch (requestError) {
      setError(requestError.response?.data?.message
        || requestError.response?.data?.error
        || 'Unable to preview this sheet.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const applyPreview = async () => {
    if (!preview?.can_apply || !canEdit) return;
    setApplying(true);
    setError('');
    try {
      const { data } = await axios.post(`/api/v1/warehouse/capacity-matrix/sheet-import/${preview.import_id}/apply`);
      setApplied(data);
      await loadState();
      onApplied?.(data);
    } catch (requestError) {
      setError(requestError.response?.data?.message
        || requestError.response?.data?.error
        || 'Unable to apply this preview.');
    } finally {
      setApplying(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SheetImportOverlay>
      <section role="dialog" aria-modal="true" aria-labelledby="capacity-sheet-sync-title" className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <header className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <h2 id="capacity-sheet-sync-title" className="text-lg font-semibold text-gray-900">Sync capacity from sheet</h2>
            <p className="mt-1 text-xs text-gray-500">Only the capacity matrix is updated. Families missing from the sheet are kept.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded border px-2 py-1 text-xs">Close</button>
        </header>
        <div className="thin-scroll min-h-0 flex-1 overflow-y-auto p-5">
          <SheetImportUrlTabs
            url={url}
            onUrlChange={(value) => { setUrl(value); setTabs([]); setTab(''); setPreview(null); }}
            tabs={tabs}
            tab={tab}
            onTabChange={(value) => { setTab(value); setPreview(null); }}
            onLoadTabs={() => loadTabs(url, tab)}
            loadingTabs={loadingTabs}
            canLoad={Boolean(url)}
            loadError={error ? <p role="alert" className="self-end text-xs text-red-700">{error}</p> : null}
            onPreview={previewSheet}
            loadingPreview={loadingPreview}
            canPreview={Boolean(url && tab)}
            canEdit={canEdit}
          />

          {preview && (
            <div className="mt-5 space-y-4">
              <section className="rounded border border-gray-200 p-3">
                <h3 className="text-sm font-semibold text-gray-900">New rows ({preview.added?.length || 0})</h3>
                {preview.added?.length ? <ul className="mt-2 divide-y divide-gray-100 text-xs">{preview.added.map((item) => (
                  <li key={itemKey(item)} className="py-2">
                    <span className="font-medium">{familyLabel(item)}</span>
                    <span className="ml-2 text-gray-600">Pallet {displayValue(item.record.pallet_max_units)} units · small bin {displayValue(item.record.sm_bin_max_units)} · large bin {displayValue(item.record.lg_bin_max_units)} · large pallet {displayValue(item.record.large_pallet_max_units)}</span>
                  </li>
                ))}</ul> : <p className="mt-2 text-xs text-gray-500">No new rows.</p>}
              </section>

              <section className="rounded border border-gray-200 p-3">
                <h3 className="text-sm font-semibold text-gray-900">Changed ({preview.changed?.length || 0})</h3>
                {preview.changed?.length ? <ul className="mt-2 divide-y divide-gray-100 text-xs">{preview.changed.map((item) => (
                  <li key={itemKey(item)} className="py-2">
                    <p className="font-medium text-gray-900">{familyLabel(item)}</p>
                    {item.fields.map((field) => <p key={field.field} className="mt-1 text-gray-600">
                      {FIELD_LABELS[field.field] || field.field}: {displayValue(field.from)} → {displayValue(field.to)}
                    </p>)}
                  </li>
                ))}</ul> : <p className="mt-2 text-xs text-gray-500">No changed families.</p>}
              </section>

              <section className="rounded border border-gray-200 p-3">
                <h3 className="text-sm font-semibold text-gray-900">Rows to fix ({preview.invalid?.length || 0})</h3>
                {preview.invalid?.length ? (
                  <>
                    <p className="mt-1 text-xs text-red-700">Fix these rows in the sheet, then preview again.</p>
                    <ul className="mt-2 space-y-1 text-xs text-red-800">{preview.invalid.map((item) => (
                      <li key={`${item.row}-${item.family || 'sheet'}-${item.scope || ''}`}>
                        Row {item.row || '—'}{item.family ? ` · ${item.family}${item.scope ? ` · ${item.scope}` : ''}` : ''}: {item.errors.join('; ')}
                      </li>
                    ))}</ul>
                  </>
                ) : <p className="mt-2 text-xs text-gray-500">No rows need fixing.</p>}
              </section>

              <section className="rounded border border-gray-200 p-3">
                <h3 className="text-sm font-semibold text-gray-900">In Eadin but not in the sheet (kept)</h3>
                {preview.eadin_only?.length ? <p className="mt-2 text-xs text-gray-600">{preview.eadin_only.join(', ')}</p> : <p className="mt-2 text-xs text-gray-500">None.</p>}
              </section>
              <p className="text-xs text-gray-600">Unchanged: {preview.unchanged || 0}</p>
            </div>
          )}

          <SheetImportHistory
            items={history}
            renderItem={(item) => <div key={item.id} className="flex flex-wrap justify-between gap-x-3 border-b py-1.5 text-gray-600">
              <span>#{item.id} · {item.status} · {item.counts?.added || 0} new, {item.counts?.changed || 0} changed</span>
              <span>{formatDate(item.applied_at || item.created_at)} · {item.actor || 'Unknown'}</span>
            </div>}
          />
        </div>
        <SheetImportFooter
          applied={Boolean(applied)}
          successLabel={applied?.message || 'Capacity table synced.'}
          onClose={onClose}
          canEdit={canEdit}
          canApply={Boolean(preview?.can_apply && !preview.invalid?.length)}
          applying={applying}
          onApply={applyPreview}
          applyLabel="Apply"
          note={preview?.invalid?.length ? 'Fix these rows in the sheet, then preview again.' : null}
        />
      </section>
    </SheetImportOverlay>
  );
};

export default CapacitySheetSyncModal;
